import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { MagneticButton } from './MagneticButton';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_shine-beauty/artifacts/9gn465w9_Buttershine%20logo%20no%20bg.png";

export const Hero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Blobs */}
      <div className="blob-container">
        <motion.div
          style={{ y: bgY }}
          className="golden-blob blob w-[600px] h-[600px] top-[-200px] right-[-100px]"
        />
        <motion.div
          style={{ y: bgY }}
          className="golden-blob blob-delayed w-[500px] h-[500px] bottom-[-150px] left-[-150px]"
        />
        <motion.div
          style={{ y: bgY }}
          className="golden-blob blob w-[400px] h-[400px] top-1/3 left-1/4"
        />
      </div>

      {/* Floating petals/ingredients */}
      <motion.div
        className="absolute top-[30%] left-[20%] w-4 h-4 bg-[#DAB668] rounded-full opacity-30 floating"
        style={{ animationDuration: '4s' }}
      />
      <motion.div
        className="absolute top-[50%] right-[25%] w-3 h-3 bg-[#DAB668] rounded-full opacity-20 floating-delayed"
        style={{ animationDuration: '5s' }}
      />
      <motion.div
        className="absolute bottom-[35%] left-[35%] w-2 h-2 bg-[#DAB668] rounded-full opacity-25 floating"
        style={{ animationDuration: '6s' }}
      />

      {/* Main Content */}
      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 max-w-5xl mx-auto px-6 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <img
            src={LOGO_URL}
            alt="Buttershine"
            className="h-20 md:h-28 mx-auto mb-8"
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.h1
          data-testid="hero-heading"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-serif text-4xl sm:text-5xl lg:text-7xl text-[#2C2C2C] leading-tight mb-6"
        >
          Beauty, <span className="gradient-text">refined</span>.
          <br />
          Wellness, <span className="gradient-text">defined</span>.
        </motion.h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-base md:text-lg text-[#6E6A62] max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          Premium beauty and wellness tools curated for everyone. 
          Discover the art of self-care with Buttershine.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <MagneticButton
            data-testid="hero-cta-primary"
            onClick={() => scrollToSection('#products')}
            className="px-8 py-4 bg-[#DAB668] text-white rounded-full text-base font-medium hover:bg-[#C5A152] transition-colors shadow-lg"
          >
            Explore Products
          </MagneticButton>

          <MagneticButton
            data-testid="hero-cta-secondary"
            onClick={() => scrollToSection('#contact')}
            className="px-8 py-4 bg-transparent border-2 border-[#DAB668] text-[#2C2C2C] rounded-full text-base font-medium hover:bg-[#DAB668] hover:text-white transition-all"
          >
            Request Quotation
          </MagneticButton>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-12 text-sm text-[#6E6A62]"
        >
          Founded by <span className="font-medium text-[#2C2C2C]">Varun Mohan</span>
        </motion.p>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        data-testid="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-[#DAB668] rounded-full flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1.5 h-1.5 bg-[#DAB668] rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
